import React from 'react';
import { companyInfo, navLinks, contactInfo, socialLinks } from '../data/mock';
import { useTheme } from '../context/ThemeContext';
import { Facebook, Twitter, Linkedin, Instagram } from 'lucide-react';

const socialIcons = {
  facebook: Facebook,
  twitter: Twitter,
  linkedin: Linkedin,
  instagram: Instagram
};

const NexmoveFooter = () => {
  const { isDark } = useTheme();

  const scrollToSection = (id) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <footer className={`border-t py-10 sm:py-12 lg:py-16 ${
      isDark ? 'bg-[#0A0F0D] border-green-900/20' : 'bg-white border-gray-100'
    }`}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-8 sm:gap-10 lg:gap-12 mb-8 sm:mb-12">
          {/* Brand */}
          <div className="col-span-2">
            <button
              onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
              className="flex items-center gap-3 mb-3 sm:mb-4 hover:opacity-80 transition-opacity"
            >
              <img 
                src={companyInfo.logoUrl} 
                alt={companyInfo.name} 
                className="h-8 sm:h-10 w-auto object-contain"
              />
            </button>
            <p className={`text-sm sm:text-base leading-relaxed max-w-sm mb-4 sm:mb-6 ${
              isDark ? 'text-gray-400' : 'text-gray-600'
            }`}>
              {companyInfo.tagline}. Bringing clean, cost-effective energy and innovative agricultural solutions to communities across Nigeria.
            </p>
            
            {/* Social Media Icons */}
            <div className="flex items-center gap-3">
              {socialLinks.map((social) => {
                const IconComponent = socialIcons[social.icon];
                return (
                  <a
                    key={social.name}
                    href={social.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={`w-9 h-9 sm:w-10 sm:h-10 rounded-full flex items-center justify-center transition-all duration-300 ${
                      isDark 
                        ? 'bg-green-900/30 text-green-400 hover:bg-green-800/50 hover:text-green-300 hover:scale-110' 
                        : 'bg-gray-100 text-gray-600 hover:bg-green-100 hover:text-green-700 hover:scale-110'
                    }`}
                    aria-label={social.name}
                  >
                    <IconComponent size={18} />
                  </a>
                );
              })}
            </div>
          </div>

          {/* Navigation */}
          <div>
            <h4 className={`font-semibold text-xs sm:text-sm uppercase tracking-wider mb-3 sm:mb-4 ${
              isDark ? 'text-white' : 'text-gray-900'
            }`}>
              Navigation
            </h4>
            <nav className="space-y-2 sm:space-y-3">
              {navLinks.map((link) => (
                <button
                  key={link.id}
                  onClick={() => scrollToSection(link.id)}
                  className={`block text-xs sm:text-sm transition-colors ${
                    isDark ? 'text-gray-400 hover:text-green-400' : 'text-gray-600 hover:text-green-600'
                  }`}
                >
                  {link.label}
                </button>
              ))}
            </nav>
          </div>

          {/* Contact */}
          <div>
            <h4 className={`font-semibold text-xs sm:text-sm uppercase tracking-wider mb-3 sm:mb-4 ${
              isDark ? 'text-white' : 'text-gray-900'
            }`}>
              Contact
            </h4>
            <div className="space-y-2 sm:space-y-3">
              <p className={`text-xs sm:text-sm ${
                isDark ? 'text-gray-400' : 'text-gray-600'
              }`}>
                {contactInfo.phones[0]}
              </p>
              <a 
                href={`mailto:${contactInfo.emails[0]}`}
                className={`text-xs sm:text-sm block transition-colors break-all ${
                  isDark ? 'text-gray-400 hover:text-green-400' : 'text-gray-600 hover:text-green-600'
                }`}
              >
                {contactInfo.emails[0]}
              </a>
            </div>
          </div>
        </div>

        {/* Bottom bar */}
        <div className={`border-t pt-6 sm:pt-8 flex flex-col sm:flex-row justify-between items-center gap-3 sm:gap-4 ${
          isDark ? 'border-green-900/20' : 'border-gray-100'
        }`}>
          <p className={`text-xs sm:text-sm text-center sm:text-left ${
            isDark ? 'text-gray-500' : 'text-gray-500'
          }`}>
            © {new Date().getFullYear()} {companyInfo.fullName}. All rights reserved.
          </p>
          <div className="flex items-center gap-2">
            <span className={`text-xs ${isDark ? 'text-gray-600' : 'text-gray-400'}`}>Powered by</span>
            <span className="gradient-text text-xs font-semibold">Matlex Media</span>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default NexmoveFooter;
